import { useEffect, useRef, useState } from 'react';

import { useEmbedScale } from '@/hooks/shared/useEmbedScale';

import BlurImage from '@/components/shared/ui/BlurImage';

import type { IndustrialLiveDemoFrameProps } from './IndustrialLiveDemoFrame.type';

/** Width and height the demo consoles lay themselves out for, in CSS pixels. */
const DEMO_VIEWPORT = { width: 1440, height: 900 } as const;

/**
 * How long the still is held after the frame's `load` event, in milliseconds.
 *
 * `load` fires when a console's document is parsed, roughly two seconds in,
 * but the consoles are single-page apps that paint once their inspection data
 * arrives — measured at eight to ten seconds from the run, on a warm backend.
 * Their own background is black, so uncovering them at `load` shows an empty
 * black panel. This value is the measured gap between the two, and the layer
 * fades rather than cuts, so a console that paints sooner is not left hidden
 * behind an opaque still.
 */
const CONSOLE_PAINT_GRACE_MS = 8000;

/**
 * How long a console has to fire `load` before it is called unavailable, in
 * milliseconds.
 *
 * A console that is down, blocked by `frame-src`, or unreachable never fires
 * `load` at all, and without this the reader is left watching a spinner that
 * never resolves. The value is far above the two seconds a healthy console
 * takes, so a slow network is not mistaken for an outage.
 */
const CONSOLE_LOAD_TIMEOUT_MS = 20000;

/**
 * ### IndustrialLiveDemoState
 *
 * How far the embedded console has got. Modelled as one value rather than a
 * pair of flags so "loaded but never requested" cannot be represented.
 *
 * - `idle` — nothing requested yet; the still stands in and invites a run
 * - `loading` — the frame is mounted and fetching; the still stays over it
 * - `live` — the console has had time to paint and is the only thing drawn
 * - `unavailable` — the console never loaded; the reader is sent to it directly
 */
type IndustrialLiveDemoState = 'idle' | 'loading' | 'live' | 'unavailable';

/**
 * ### IndustrialLiveDemoFrame
 *
 * Embeds an inspection demo console, scaled to fit the page.
 *
 * Each console is a separate application laid out for a desktop viewport and
 * cannot reflow, so it is rendered at {@link DEMO_VIEWPORT} and scaled down
 * rather than given the container's width — squeezing it would clip its own
 * panels. It loads only once a reader asks for it, so a visitor scrolling past
 * never opens a session against a demo backend, and the still stays in place
 * until the console paints, because that first load takes several seconds and
 * the console's own background is black.
 *
 * Remounting this component with a different `demoUrl` starts again from the
 * still; the caller keys it per console so switching consoles cannot leave the
 * previous one running behind the new still.
 *
 * @param props The console, its still, and the copy shown over it
 * @returns The rendered demo frame element
 * @see {@link IndustrialLiveDemoFrameProps} What each field decides
 */
export default function IndustrialLiveDemoFrame({
  demoUrl,
  poster,
  frameTitle,
  runLabel,
  loadingLabel,
  unavailableLabel,
  openLabel,
}: IndustrialLiveDemoFrameProps) {
  const [state, setState] = useState<IndustrialLiveDemoState>('idle');
  /* 한 번에 하나만 돈다: loading 동안은 로드 타임아웃, load 이후는 페인트 유예. */
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { containerRef, scale } = useEmbedScale<HTMLDivElement>(
    DEMO_VIEWPORT.width,
  );

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  function startRun() {
    setState('loading');
    timerRef.current = setTimeout(() => {
      setState('unavailable');
    }, CONSOLE_LOAD_TIMEOUT_MS);
  }

  function handleFrameLoaded() {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      setState('live');
    }, CONSOLE_PAINT_GRACE_MS);
  }

  return (
    <div
      ref={containerRef}
      className="group relative overflow-hidden rounded-[20px] border border-[rgba(255,255,255,0.14)] bg-[#0B0B0D]"
      style={{
        aspectRatio: `${DEMO_VIEWPORT.width} / ${DEMO_VIEWPORT.height}`,
      }}
    >
      {state !== 'idle' && (
        <iframe
          src={demoUrl}
          title={frameTitle}
          allow="autoplay"
          onLoad={handleFrameLoaded}
          className="block border-0"
          style={{
            width: `${DEMO_VIEWPORT.width}px`,
            height: `${DEMO_VIEWPORT.height}px`,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}
        />
      )}
      {/*
        live 가 되어도 DOM 에서 빼지 않고 투명하게만 만든다. 콘솔이 칠해지는
        순간을 잘라내듯 드러내면 검은 패널이 그대로 보인다.
      */}
      <div
        aria-hidden={state === 'live'}
        className={`absolute inset-0 transition-opacity duration-500 motion-reduce:transition-none ${
          state === 'live' ? 'pointer-events-none opacity-0' : 'opacity-100'
        }`}
      >
        <div className="absolute inset-0">
          {/*
            스틸은 콘솔을 그 뷰포트 그대로 찍은 것이라 프레임과 비율이 같다.
            불러오는 동안에는 흐리게 덮어, 멈춘 화면을 실시간 화면으로
            읽지 않게 한다.
          */}
          <BlurImage
            src={poster.src}
            width={poster.width}
            height={poster.height}
            alt=""
            quality={100}
            /*
              프레임은 Area(max-w 1520px, gutter 20px) 안쪽을 그대로 채운다.
              1520px 이상에서 1480px 로 고정되고 그 아래로는 뷰포트에서
              gutter 만 뺀 폭이다.
            */
            sizes="(min-width: 1520px) 1480px, calc(100vw - 40px)"
            className={`h-full w-full object-cover transition-[filter,transform] duration-500 motion-reduce:transition-none ${
              state === 'loading' ? 'scale-[1.02] blur-[6px]' : ''
            }`}
          />
          {state === 'idle' ? (
            /*
             * md 아래에서는 콘솔이 0.25배 이하로 줄어 읽을 수 없으므로 실행
             * 자체를 내보내지 않고 이미지만 남긴다.
             *
             * 레이어 자체가 버튼이다. 이미지 어디를 눌러도 실행되고 커서도
             * 전체에서 포인터가 된다. 알약은 그 안의 장식이라 따로 포커스를
             * 받지 않는다.
             *
             * 포인터를 올렸을 때만 나오지만, 호버가 없는 기기(md 이상 태블릿)
             * 에서는 드러낼 방법이 없어 처음부터 보여주고, 키보드 사용자는
             * focus-visible 로 같은 레이어를 연다.
             */
            <button
              type="button"
              onClick={startRun}
              className="absolute inset-0 hidden w-full cursor-pointer touch-manipulation items-center justify-center bg-[rgba(11,11,13,0.55)] px-[20px] text-center opacity-0 transition-opacity duration-300 motion-reduce:transition-none focus-visible:opacity-100 focus-visible:outline-2 focus-visible:-outline-offset-[3px] focus-visible:outline-apps-corePrimary01 group-hover:opacity-100 md:flex [@media(hover:none)]:opacity-100"
            >
              <span className="inline-flex min-h-[48px] items-center gap-[10px] rounded-[999px] bg-apps-coreGrayWhite px-[24px] text-[16px] font-bold leading-[140%] text-apps-coreGrayBlack lg:text-[18px]">
                <svg
                  viewBox="0 0 10 12"
                  aria-hidden
                  className="h-[12px] w-[10px]"
                >
                  <path d="M0 0l10 6-10 6z" fill="currentColor" />
                </svg>
                {runLabel}
              </span>
            </button>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-[16px] bg-[rgba(11,11,13,0.55)] px-[20px] text-center">
              {state === 'unavailable' ? (
                <>
                  <p
                    role="status"
                    className="text-[16px] font-bold leading-[140%] text-apps-coreGrayWhite lg:text-[18px]"
                  >
                    {unavailableLabel}
                  </p>
                  <a
                    href={demoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex min-h-[48px] touch-manipulation items-center gap-[8px] rounded-[999px] bg-apps-coreGrayWhite px-[24px] text-[16px] font-bold leading-[140%] text-apps-coreGrayBlack focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-apps-corePrimary01"
                  >
                    {openLabel}
                    <span aria-hidden>↗</span>
                  </a>
                </>
              ) : (
                <>
                  {/* 회전은 globals.css 의 .industrial-demo-spinner 가 쥔다. */}
                  <span
                    aria-hidden
                    className="industrial-demo-spinner block h-[40px] w-[40px] rounded-full border-[3px] border-[rgba(255,255,255,0.25)] border-t-apps-coreGrayWhite"
                  />
                  <p
                    role="status"
                    className="text-[16px] font-bold leading-[140%] text-apps-coreGrayWhite lg:text-[18px]"
                  >
                    {loadingLabel}
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
